import { ReactNode, RefObject, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { config } from '../../../utils/config';
import getHash from '../../../utils/getHash';
import useIsMobileDevice from '../../../utils/useIsMobileDevice';
import useScroll from '../../../utils/useScroll';
import { wait } from '../../../utils/wait';
import Notification from '../../atoms/Notification';
import './style.scss';

export type NotificationLevel = 'success' | 'info' | 'warning' | 'error';

export type NotificationOptions = {
  id: string;
  text: ReactNode;
  level: NotificationLevel;
  autoClose: boolean;
  closable: boolean;
};

const MAX_NOTIFICATIONS = 3;
const AUTO_HIDE_DURATION_IN_MILLISECONDS = 10000;

const getNotificationId = async (options: Omit<NotificationOptions, 'id'>) =>
  (await getHash(JSON.stringify({ ...options, date: new Date() }))).substring(0, 12);

export let showNotification: (
  options: Omit<NotificationOptions, 'id' | 'autoClose' | 'closable'> & { autoClose?: boolean; closable?: boolean }
) => Promise<void>;
export let showDefaultError: () => Promise<void>;

const calculateHeaderOffset = (configHeaderOffset: number, headerElement?: string) => {
  if (!headerElement) {
    return configHeaderOffset;
  }

  const element = document.querySelector(headerElement)?.getBoundingClientRect();

  if (!element) {
    return configHeaderOffset;
  }

  return Math.max(element.height + element.top, 0);
};

const Notifications = ({
  containerRef,
  headerElement
}: {
  containerRef: RefObject<HTMLUListElement>;
  headerElement?: string;
}) => {
  const isMobile = useIsMobileDevice();
  const configHeaderOffset = isMobile ? config.header.mobileHeight : config.header.desktopHeight;
  const [offsetHeight, setOffsetHeight] = useState(calculateHeaderOffset(configHeaderOffset, headerElement));

  const [isRemoving, setIsRemoving] = useState(0);
  const [notificationsState, setNotificationsState] = useState<NotificationOptions[]>([]);
  const notifications = useRef<NotificationOptions[]>([]);
  const notificationsQueue = useRef<NotificationOptions[]>([]);
  const { formatMessage } = useIntl();

  useScroll(() => {
    setOffsetHeight(calculateHeaderOffset(configHeaderOffset, headerElement));
  });

  const closeNotification = async (id: string, index: number) => {
    if (!containerRef.current?.children) {
      return;
    }

    setIsRemoving(index + 1);

    const closedNotificationElement = Array.from(containerRef.current.children).find(e => e.id === id)!;

    closedNotificationElement?.classList?.add('notification--remove');

    await wait(300);

    // Remove closed notification
    const remainingNotifications = notifications.current.filter(n => n.id !== id);

    // In case of having notifications in the queue, we move the first one into notifications
    if (notificationsQueue.current.length) {
      const newNotificationsQueue = notificationsQueue.current;
      const firstNotificationInTheQueue = newNotificationsQueue.shift()!;
      notificationsQueue.current = newNotificationsQueue;
      remainingNotifications.push(firstNotificationInTheQueue);

      notifications.current = remainingNotifications;
      setNotificationsState(remainingNotifications);

      // Autoclose the notification from queue if necessary
      if (firstNotificationInTheQueue.autoClose) {
        await wait(isRemoving ? 300 * 2 : 300);
        closeNotification(firstNotificationInTheQueue.id, remainingNotifications.length - 1);
      }
    } else {
      notifications.current = remainingNotifications;
      setNotificationsState(remainingNotifications);
    }
    setIsRemoving(0);
  };

  showNotification = async ({ level, text, closable = true, autoClose = false }) => {
    const id = await getNotificationId({ level, text, closable, autoClose });
    const newNotification = { id, level, text, closable, autoClose };

    notifications.current = notifications.current.filter(notification => notification.text !== text);

    // If we reach the MAX_NOTIFICATIONS, we add the new notification into the notificationsQueue
    if (notifications.current.length < MAX_NOTIFICATIONS) {
      notifications.current = [...notifications.current, newNotification];
      setNotificationsState(notifications.current);

      if (autoClose) {
        await wait(AUTO_HIDE_DURATION_IN_MILLISECONDS);
        closeNotification(id, notifications.current.length - 1);
      }
    } else {
      notificationsQueue.current = [...notificationsQueue.current, newNotification];
    }
  };
  showDefaultError = () =>
    showNotification({
      level: 'error',
      text: formatMessage({ id: 'errors.default' }),
      autoClose: true
    });

  return (
    <ul
      className="notification-container"
      ref={containerRef}
      style={{
        top: IS_WEB_COMPONENT ? offsetHeight : 0,
        left: IS_WEB_COMPONENT ? 0 : 'auto',
        position: IS_WEB_COMPONENT ? 'fixed' : 'sticky'
      }}
    >
      {notificationsState.map((n, i) => (
        <Notification {...n} onClose={() => closeNotification(n.id, i)} key={n.id} />
      ))}
    </ul>
  );
};

export default Notifications;
